import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PwaInstallModal } from 'components/PwaInstallModal';
import routes from 'routes';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { logoutRequest } from 'store/auth/actions';
import { getAuthUser } from 'store/auth/selectors';
import { getProfilePictures } from 'store/app/selectors';
import {
  applyThemePreference,
  getStoredThemePreference,
  getSystemTheme,
  persistThemePreference,
  type AppTheme,
  type ThemePreference,
} from 'utils/theme-preference';
import {
  isStandalonePwa,
  isIosDevice,
  isSamsungInternetBrowser,
} from 'utils/pwa';
import { runPwaInstallFromUserGesture } from 'utils/pwa-install-flow';
import { usePwaDeferredInstallPrompt } from 'utils/usePwaDeferredInstallPrompt';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { PwaIcon } from './Icon';
import {
  PwaChip,
  PwaRow,
  PwaSection,
  PwaSegmented,
  PwaTopBar,
  PwaUserPic,
} from './ui';

/** PWA More Appearance UI only — does not change desktop theme-preference behaviour. */
const PWA_THEME_UI_KEY = 'pwa-theme-ui-preference';

function readPwaThemeUi(): ThemePreference {
  if (typeof window === 'undefined') return getStoredThemePreference();
  try {
    const raw = window.localStorage.getItem(PWA_THEME_UI_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* ignore */
  }
  return getStoredThemePreference();
}

function writePwaThemeUi(value: ThemePreference): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PWA_THEME_UI_KEY, value);
  } catch {
    /* ignore */
  }
}

function applyPwaThemeChoice(value: ThemePreference): AppTheme {
  if (value === 'system') {
    const resolved = getSystemTheme();
    applyThemePreference(resolved);
    persistThemePreference(resolved);
    return resolved;
  }
  applyThemePreference(value);
  persistThemePreference(value);
  return value;
}

function initialsFromName(first?: string, last?: string, email?: string): string {
  const a = String(first ?? '').trim().charAt(0);
  const b = String(last ?? '').trim().charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  const e = String(email ?? '').trim().charAt(0);
  return e ? e.toUpperCase() : '?';
}

/** Match ProfileCard — pics are often raw base64 without a data: prefix. */
function profilePictureSrc(picture: string | null | undefined): string | null {
  const raw = String(picture ?? '').trim();
  if (!raw) return null;
  if (raw.startsWith('data:') || raw.startsWith('http') || raw.startsWith('blob:')) {
    return raw;
  }
  if (/\.(svg|png|jpe?g|gif|webp|ico)(\?|$)/i.test(raw)) return raw;
  return `data:image/png;base64,${raw}`;
}

function accountTypeLabel(
  accountType: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const key = String(accountType ?? '').trim().toUpperCase();
  if (key === 'OWNER') {
    return t('users.account-type-owner', { defaultValue: 'Owner' });
  }
  if (key === 'STAFF') {
    return t('users.account-type-staff', { defaultValue: 'Staff' });
  }
  if (key === 'CLIENT') {
    return t('users.account-type-client', { defaultValue: 'Client' });
  }
  return (
    String(accountType ?? '').trim() ||
    t('users.user', { defaultValue: 'User' })
  );
}

export const PwaMorePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUser);
  const profilePictures = useSelector(getProfilePictures);

  const [theme, setTheme] = React.useState<ThemePreference>(readPwaThemeUi);
  const [installModalOpen, setInstallModalOpen] = React.useState(false);
  const [installFallbackStep, setInstallFallbackStep] = React.useState<
    'ios-guide' | 'android-manual' | 'use-chrome' | undefined
  >(undefined);

  const showAddToHomeScreen = !isStandalonePwa();
  const deferredInstallPrompt = usePwaDeferredInstallPrompt();
  const canNativeAndroidInstall =
    !isIosDevice() &&
    !isSamsungInternetBrowser() &&
    Boolean(deferredInstallPrompt);
  const installLabel = isIosDevice()
    ? t('navigation.add-to-home-screen')
    : canNativeAndroidInstall
      ? t('navigation.install-app')
      : t('navigation.add-to-home-screen');

  const storedPic = React.useMemo(() => {
    if (!user?.userUuid || !profilePictures?.length) return null;
    return (
      profilePictures.find((pp) => pp?.userUuid === user.userUuid)
        ?.profilePicture ?? null
    );
  }, [profilePictures, user?.userUuid]);

  const pictureSrc = profilePictureSrc(
    storedPic || user?.profilePicture || null
  );

  React.useEffect(() => {
    if (!user?.userUuid) return;
    if (!user.hasProfilePicture) return;
    if (pictureSrc) return;
    dispatch(fetchProfilePictureRequest(user.userUuid));
  }, [dispatch, user?.userUuid, user?.hasProfilePicture, pictureSrc]);

  // Follow OS only while PWA Appearance is set to System (PWA-local key).
  React.useEffect(() => {
    if (theme !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (readPwaThemeUi() !== 'system') return;
      applyPwaThemeChoice('system');
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [theme]);

  const displayName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || user?.email || '—';
  const initials = initialsFromName(
    user?.firstName,
    user?.lastName,
    user?.email
  );
  const role = accountTypeLabel(user?.accountType, t);
  const companyTz = [user?.companyName, user?.timezone]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean)
    .join(' · ');
  const lastLoginLabel = user?.lastLogin
    ? formatDisplayDate(user.lastLogin, 'D MMM YYYY, HH:mm', i18n.language)
    : null;

  const openView = (view: string) => {
    navigate(`${routes.account.root}?view=${view}`);
  };

  const onThemeChange = (value: string) => {
    const next: ThemePreference =
      value === 'dark' || value === 'system' ? value : 'light';
    setTheme(next);
    writePwaThemeUi(next);
    applyPwaThemeChoice(next);
  };

  const handleInstall = () => {
    void runPwaInstallFromUserGesture().then((result) => {
      if (result.type === 'android-started') return;
      if (result.type === 'ios-guide') {
        setInstallFallbackStep('ios-guide');
      } else if (result.type === 'use-chrome') {
        setInstallFallbackStep('use-chrome');
      } else {
        setInstallFallbackStep('android-manual');
      }
      setInstallModalOpen(true);
    });
  };

  return (
    <div className="pwa-more-page" data-shell="pwa">
      <PwaTopBar logo />
      <main className="pwa-main">
        <div style={{ height: 16 }} />
        <PwaSection>
          <button
            type="button"
            className="pwa-card pwa-profile"
            onClick={() => openView('details')}
          >
            <PwaUserPic size="xl" initials={initials} src={pictureSrc} />
            <span className="pwa-row-body">
              <span className="pwa-row-title pwa-profile__name">
                {displayName}
              </span>
              <span className="pwa-row-sub">{user?.email}</span>
              <span className="pwa-profile__role">
                <PwaChip tone="role">{role}</PwaChip>
              </span>
            </span>
            <span className="pwa-profile__chevron" aria-hidden>
              <PwaIcon name="chevron" size={18} />
            </span>
          </button>
        </PwaSection>

        <PwaSection title={t('users.account', { defaultValue: 'Account' })}>
          <div className="pwa-list">
            <PwaRow
              icon="user"
              title={t('users.personal-details', {
                defaultValue: 'Personal details',
              })}
              sub={companyTz || undefined}
              chevron
              onClick={() => openView('details')}
            />
            <PwaRow
              icon="shield_check"
              title={t('users.account-security', {
                defaultValue: 'Account & security',
              })}
              sub={t('users.account-security-sub', {
                defaultValue: 'Sign-in, permissions, linked accounts',
              })}
              chevron
              onClick={() => openView('security')}
            />
            <PwaRow
              icon="history"
              title={t('users.login-history', {
                defaultValue: 'Login history',
              })}
              sub={
                lastLoginLabel
                  ? t('users.last-sign-in', {
                      defaultValue: 'Last sign-in {{at}}',
                      at: lastLoginLabel,
                    })
                  : undefined
              }
              chevron
              onClick={() => openView('history')}
            />
          </div>
        </PwaSection>

        <PwaSection title={t('navigation.app', { defaultValue: 'App' })}>
          <div className="pwa-list">
            <PwaRow
              icon="eye"
              title={t('navigation.accessibility')}
              sub={t('users.accessibility-sub', {
                defaultValue: 'Text size, contrast, motion',
              })}
              chevron
              onClick={() => navigate(routes.accessibility)}
            />
            {showAddToHomeScreen ? (
              <PwaRow
                icon="download"
                title={installLabel}
                sub={t('users.install-sub', {
                  defaultValue: 'Add generate.TAX to your home screen',
                })}
                chevron
                onClick={handleInstall}
              />
            ) : null}
            <div className="pwa-row-block">
              <div className="pwa-profile__theme-row">
                <span className="pwa-row-ico" aria-hidden>
                  <PwaIcon name="sun" size={20} />
                </span>
                <span className="pwa-row-body">
                  <span className="pwa-row-title">
                    {t('users.appearance', { defaultValue: 'Appearance' })}
                  </span>
                  <span className="pwa-row-sub">
                    {t('users.appearance-sub', {
                      defaultValue: 'Saved on this device',
                    })}
                  </span>
                </span>
              </div>
              <PwaSegmented
                label={t('users.theme', { defaultValue: 'Theme' })}
                value={theme}
                onChange={onThemeChange}
                options={[
                  {
                    value: 'light',
                    label: t('users.theme-light', { defaultValue: 'Light' }),
                  },
                  {
                    value: 'dark',
                    label: t('users.theme-dark', { defaultValue: 'Dark' }),
                  },
                  {
                    value: 'system',
                    label: t('users.theme-system', { defaultValue: 'System' }),
                  },
                ]}
              />
            </div>
          </div>
        </PwaSection>

        <PwaSection>
          <button
            type="button"
            className="pwa-btn danger block"
            onClick={() => dispatch(logoutRequest())}
          >
            <PwaIcon name="logout" size={19} />
            {t('navigation.logout')}
          </button>
          <p className="pwa-row-meta pwa-more-page__version">
            {t('users.app-version-pwa', {
              defaultValue: 'generate.TAX version 4.2 (PWA)',
            })}
          </p>
        </PwaSection>
      </main>

      <PwaInstallModal
        open={installModalOpen}
        initialStep={installFallbackStep}
        onClose={() => {
          setInstallModalOpen(false);
          setInstallFallbackStep(undefined);
        }}
      />
    </div>
  );
};

export default PwaMorePage;
